import React, { useState, useMemo, useEffect } from 'react';
import { X, Send, CreditCard, Lock } from 'lucide-react';
import BookingDetailsForm from './BookingDetailsForm';
import { Card } from './ui/card';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from './ui/button';

import { BACKEND_URL, getStripePublishableKey } from '../config'  // Auto-fixed: use centralized config;

// Lazy Stripe.js loader. See PaymentModal.jsx for the full rationale —
// short version: `@stripe/stripe-js` auto-preloads Stripe.js as a
// side-effect of `import`, which surfaces in Sentry as
// "Failed to load Stripe.js" on bots / blocked networks (issue 7476660220).
// We dynamic-import the library inside the loader so it only runs once a
// guest actually opens the booking request modal, and swallow the
// rejection at the source so it never bubbles unhandled.
let _stripePromise = null;
const getStripePromise = () => {
  if (_stripePromise) return _stripePromise;
  _stripePromise = Promise.all([
    import('@stripe/stripe-js'),
    getStripePublishableKey(),
  ])
    .then(([{ loadStripe }, key]) => loadStripe(key))
    .catch((err) => {
      console.error('Stripe.js failed to load:', err);
      return null;
    });
  return _stripePromise;
};

// Payment Form Component
const RequestPaymentForm = ({ clientSecret, onSuccess, onError, loading, customerInfo, totalPrice }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // Confirm the payment (but don't capture - it's in manual mode)
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: customerInfo.name,
              email: customerInfo.email,
              phone: customerInfo.phone,
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        onError(error.message);
      } else if (paymentIntent && paymentIntent.status === 'requires_capture') {
        // Payment method authorized successfully!
        onSuccess(paymentIntent.id);
      } else {
        onError('Payment authorization failed. Please try again.');
      }
    } catch (err) {
      onError(err.message || 'An error occurred during payment authorization');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Payment Authorization</h4>
            <p className="text-sm text-blue-700">
              Your card will be authorized for <strong>CA${totalPrice.toFixed(2)}</strong> but 
              <strong> not charged</strong> until the host accepts your request. If declined, the authorization 
              will be cancelled automatically.
            </p>
          </div>
        </div>
      </div>

      <PaymentElement />

      <Button
        type="submit"
        disabled={!stripe || processing || loading}
        className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3"
      >
        {processing ? (
          <span className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            Authorizing...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <CreditCard className="w-5 h-5" />
            Authorize Card & Send Request
          </span>
        )}
      </Button>
    </form>
  );
};

const RequestToBookModal = ({ isOpen, onClose, onSubmit, property, checkIn, checkOut, guests, totalPrice, bookingType, selectedRoom, pricePerNight = null, accommodationSubtotal = null, discountAmount = 0, promoCode = null, promoSavings = 0 }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('details'); // 'details' or 'payment'
  const [customerInfo, setCustomerInfo] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');

  // Lazy-build the Stripe promise the first time this modal mounts open.
  // Stable across re-renders. Never fires on routes that don't render
  // RequestToBookModal (i.e. anywhere except a property detail page).
  const [stripePromise, setStripePromise] = useState(() => (isOpen ? getStripePromise() : null));
  useEffect(() => {
    if (isOpen && !stripePromise) setStripePromise(getStripePromise());
  }, [isOpen, stripePromise]);

  // Pull logged-in user from localStorage so the details step can be
  // pre-filled (or skipped) when the profile already has every required
  // field. Saves a redundant data-entry step for signed-in guests.
  const guestDefaults = useMemo(() => {
    try {
      const raw = localStorage.getItem('user_data');
      const u = raw ? JSON.parse(raw) : null;
      if (!u) return null;
      return {
        fullName: u.full_name || '',
        firstName: u.first_name || '',
        lastName: u.last_name || '',
        email: u.email || '',
        phone: u.phone || '',
      };
    } catch {
      return null;
    }
  }, [isOpen]); // re-read whenever the modal opens

  useEffect(() => {
    if (!isOpen || !guestDefaults || customerInfo) return;
    const fullName = (guestDefaults.fullName || `${guestDefaults.firstName || ''} ${guestDefaults.lastName || ''}`).trim();
    const phone = (guestDefaults.phone || '').trim();
    const email = (guestDefaults.email || '').trim();
    if (fullName && email && phone) {
      // Auto-skip the details step when the profile is complete.
      handleDetailsSubmit({
        customerName: fullName,
        customerEmail: email,
        customerPhone: phone,
        message: '',
      });
    }
  }, [isOpen, guestDefaults]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDetailsSubmit = async (formData) => {
    setLoading(true);
    setError('');
    
    try {
      // VALIDATE: Check minimum nights requirement
      if (checkIn && checkOut) {
        const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
        const minimumNights = property.minimum_nights || 1;
        
        if (nights < minimumNights) {
          setError(`This property requires a minimum stay of ${minimumNights} ${minimumNights === 1 ? 'night' : 'nights'}. You selected ${nights} ${nights === 1 ? 'night' : 'nights'}. Please go back and adjust your dates.`);
          setLoading(false);
          return;
        }
      }
      
      // Store customer info
      setCustomerInfo({
        name: formData.customerName,
        email: formData.customerEmail,
        phone: formData.customerPhone,
        message: formData.message,
      });

      console.log('Creating PaymentIntent for request booking...');
      console.log('Booking details:', { 
        bookingType, 
        roomNumber: selectedRoom?.room_number,
        checkIn: checkIn?.toISOString().split('T')[0],
        checkOut: checkOut?.toISOString().split('T')[0]
      });
      
      // Normalize guests to total int (adults + children) so backend per-guest pricing lookup works
      const totalGuests = (typeof guests === 'object' && guests !== null)
        ? (Number(guests.adults) || 0) + (Number(guests.children) || 0)
        : Number(guests) || 1;

      // Create PaymentIntent with manual capture
      const userToken = localStorage.getItem('user_token') || localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/payments/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
        },
        body: JSON.stringify({
          property_id: property._id || property.id,
          check_in: checkIn ? checkIn.toISOString().split('T')[0] : null,
          check_out: checkOut ? checkOut.toISOString().split('T')[0] : null,
          guests: totalGuests,
          amount: totalPrice,
          customer_name: formData.customerName,
          customer_email: formData.customerEmail,
          customer_phone: formData.customerPhone,
          booking_type: bookingType || 'full_property', // Use the actual booking type (full_property or single_room)
          room_number: selectedRoom?.room_number || null,
          metadata: {
            message: formData.message || '',
            booking_mode: 'request', // This is the booking mode (instant vs request)
            // CRITICAL: Pass frontend-computed pricing so backend saves the SAME values Stripe authorized
            ...(pricePerNight != null && { price_per_night: String(pricePerNight) }),
            ...(accommodationSubtotal != null && { accommodation_subtotal: String(accommodationSubtotal) }),
            ...(discountAmount != null && { discount_amount: String(discountAmount) }),
            // Promo discount: record on the booking AND advertise the savings
            // so the host's booking detail page shows the promo properly.
            // Sentry / user-reported: promo was visually applied but the
            // Stripe authorization came back at the un-discounted total.
            ...(promoCode && { promo_code: String(promoCode) }),
            ...(promoSavings > 0 && { promo_savings: String(promoSavings) }),
            ...(selectedRoom?.name && { room_name: selectedRoom.name })
          },
        }),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to create payment intent';
        try {
          const errorData = await response.json();
          const detail = errorData?.detail;
          if (response.status === 403 && detail?.error_code === 'id_verification_required') {
            // Bubble a structured error so BookingWidget can open the verification modal
            const err = new Error(detail.message || 'ID verification required');
            err.verificationRequired = true;
            err.status = 403;
            throw err;
          }
          errorMessage = typeof detail === 'string' ? detail : (detail?.message || errorMessage);
        } catch (parseError) {
          if (parseError?.verificationRequired) throw parseError;
          // If JSON parsing fails, use status text
          errorMessage = `${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('PaymentIntent created:', data.payment_intent_id);
      
      setClientSecret(data.client_secret);
      setStep('payment');
    } catch (error) {
      console.error('PaymentIntent creation error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    setLoading(true);
    setError('');
    try {
      // Submit the booking request with payment intent ID
      await onSubmit({
        ...customerInfo,
        payment_intent_id: paymentIntentId,
      });
      // Success! The parent component (BookingWidget) will handle toast, modal close & redirect
      // Don't call onClose() here - let parent handle it via setShowRequestModal(false)
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Send className="w-6 h-6 text-blue-600" />
                Request to Book
              </h2>
              <p className="text-sm text-gray-600">
                {step === 'details' 
                  ? "Fill in your details to continue" 
                  : "Authorize your card - you won't be charged until the host approves"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Step 1: Customer Details */}
          {step === 'details' && (
            <BookingDetailsForm
              onSubmit={handleDetailsSubmit}
              loading={loading}
              submitButtonText="Continue to Payment"
              property={property}
              checkIn={checkIn}
              checkOut={checkOut}
              guests={guests}
              totalPrice={totalPrice}
              showPropertySummary={true}
              bookingType={bookingType}
              roomName={selectedRoom?.name}
              roomNumber={selectedRoom?.room_number}
              defaults={guestDefaults}
            />
          )}

          {/* Step 2: Payment Authorization */}
          {step === 'payment' && clientSecret && (
            <div>
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Booking Summary</h3>
                <div className="text-sm space-y-1">
                  {property?.title && (
                    <p>
                      <strong>Property:</strong>{' '}
                      {property.title}
                      {bookingType === 'single_room' && (selectedRoom?.name || selectedRoom?.room_number != null) && (
                        <> &ndash; {selectedRoom.name || `Room ${selectedRoom.room_number}`}</>
                      )}
                    </p>
                  )}
                  <p><strong>Guest:</strong> {customerInfo.name}</p>
                  <p><strong>Email:</strong> {customerInfo.email}</p>
                  <p><strong>Total:</strong> CA${totalPrice.toFixed(2)}</p>
                </div>
              </div>

              {/* Cancellation Policy Warning in Payment Step */}
              {property?.cancellation_policy && (
                <div className={`mb-4 border rounded-lg p-3 text-xs ${
                  property.cancellation_policy === 'non_refundable' 
                    ? 'bg-red-50 border-red-200' 
                    : property.cancellation_policy === 'strict'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start gap-2">
                    <span className="text-base flex-shrink-0">
                      {property.cancellation_policy === 'non_refundable' && '🚫'}
                      {property.cancellation_policy === 'strict' && '🔒'}
                      {property.cancellation_policy === 'moderate' && '⚖️'}
                      {property.cancellation_policy === 'flexible' && 'ℹ️'}
                    </span>
                    <div className="flex-1">
                      <p className={`font-semibold mb-1 ${
                        property.cancellation_policy === 'non_refundable' 
                          ? 'text-red-900' 
                          : property.cancellation_policy === 'strict'
                            ? 'text-orange-900'
                            : 'text-blue-900'
                      }`}>
                        Cancellation: {property.cancellation_policy === 'non_refundable' ? 'Non-Refundable' : property.cancellation_policy.charAt(0).toUpperCase() + property.cancellation_policy.slice(1)}
                      </p>
                      <p className={`${
                        property.cancellation_policy === 'non_refundable' 
                          ? 'text-red-800' 
                          : property.cancellation_policy === 'strict'
                            ? 'text-orange-800'
                            : 'text-blue-800'
                      }`}>
                        {property.cancellation_policy === 'flexible' && 'Free cancellation up to 24 hours before check-in'}
                        {property.cancellation_policy === 'moderate' && 'Free cancellation up to 5 days before check-in'}
                        {property.cancellation_policy === 'strict' && 'Free cancellation up to 14 days before check-in'}
                        {property.cancellation_policy === 'non_refundable' && 'No cancellations or refunds allowed under any circumstances.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <RequestPaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  loading={loading}
                  customerInfo={customerInfo}
                  totalPrice={totalPrice}
                />
              </Elements>

              <button
                onClick={() => { setCustomerInfo(null); setStep('details'); }}
                className="mt-4 text-sm text-gray-600 hover:text-gray-900"
                data-testid="rtb-edit-guest-details"
              >
                ← Edit guest details
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default RequestToBookModal;

